import {
  authService,
  AvailableLoginContext,
  LoginContext,
  setCognitoContext,
  useToast,
} from '@aslanpay/portal-common-lib';

import {
  clearPendingSsoContext,
  setPendingSsoContext,
  setPendingSsoPhoneNumberRequired,
} from 'app/constants/authentication';
import useAuthContext from 'app/hooks/useAuthContext';

const PORTAL_LABELS: Record<LoginContext, string> = {
  ADMIN: 'admin',
  EMPLOYEE: 'employee',
};

/**
 * Starts a hosted-UI SSO sign-in for a portal switch. The pending context is persisted before
 * redirecting because in-memory state doesn't survive the full-page round trip; SSOCallback restores
 * it on the way back. Shared by the portal switcher menu (admin target, resolved up front) and the
 * employee-switch intro screen (employee target, resolved from check-email).
 */
const useSsoContextSwitch = () => {
  const toast = useToast();
  const { activeLoginContext } = useAuthContext();

  return async ({ context, idpProviderName, phoneNumberRequired }: AvailableLoginContext) => {
    if (!idpProviderName) {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Configuration Error',
        description: 'SSO provider information is missing. Please contact support.',
      });
      return;
    }

    try {
      setCognitoContext(context);
      setPendingSsoContext(context);
      setPendingSsoPhoneNumberRequired(phoneNumberRequired === true);
      await authService.federatedSignIn({ idpProviderName });
    } catch {
      clearPendingSsoContext();
      setPendingSsoPhoneNumberRequired(false);
      setCognitoContext(activeLoginContext);
      toast({
        position: 'top-right',
        status: 'error',
        title: 'SSO Sign-in Failed',
        description: `Could not start sign-in for the ${PORTAL_LABELS[context]} portal. Please try again.`,
      });
    }
  };
};

export default useSsoContextSwitch;
